<template>
  <!-- No BlockFieldset: the block has no fields; viewer downloads, editor also builds and deletes. -->
  <div :class="`project-${protocol.id}-build`">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <b-row v-else>
      <b-col lg="8" md="8" sm="12">
        <b-row>
          <b-col lg="4" md="7" sm="7">
            <AttachmentList
              :attachments="pdfAttachments"
              :attachmentType="pdfAttachmentType"
              :deletable="!isBlockReadOnly"
              :hidesDeleteForGuests="!isOrganizationRoleNavigationEnabled"
              v-on:attachment-list:delete="onDeleteAttachment"
            ></AttachmentList>
          </b-col>
          <b-col lg="8" md="5" sm="5">
            <router-link
              class="btn btn-success pull-right"
              :to="{
                name: protocol.build.printRoute,
                params: { projectNumber: project.number },
              }"
              target="_blank"
            >
              <i class="fa fa-file mr-2"></i>{{ $t('preview') }}
            </router-link>
            <ButtonWait
              v-if="!isBlockReadOnly && !isHiddenForGuest"
              :startCallback="startReportBuild"
              :pollCallback="fetchBuildState"
              class="pull-right mr-2"
            >
              <template v-slot:buttonText>
                {{ $t('generate_report') }}
              </template>
            </ButtonWait>
          </b-col>
        </b-row>
      </b-col>
      <b-col v-if="hasDownloads" lg="4" md="4" sm="12">
        <legend>
          <strong>{{ $t('downloads') }}</strong>
        </legend>
        <b-row>
          <b-col lg="10" md="10" sm="10">
            {{ $t(protocol.build.zipLabelKey) }}
          </b-col>
          <b-col lg="2" md="2" sm="2">
            <ZipDownload
              :attachmentType="protocol.build.zipAttachmentType"
              :attachments="project[protocol.build.zipField]"
              :project="project"
              :fetchBuildState="reloadBlock"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AttachmentList from '@/components/AttachmentList';
import ButtonWait from '@/components/ButtonWait.vue';
import ZipDownload from '@/components/ZipDownload';
import { buildStateOfType } from '@/helpers/attachmentBuildState';
import { attachmentsOfType } from '@/helpers/attachmentsOfType';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/** Number of the unsaved project (ProjectEdit `_new`). */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block "<Protokoll>: Bericht generieren" of a protocol tab (see feature page projekt-protokolle):
 * the generated PDF with preview, build and delete, and the ZIP of the protocol photos when the
 * protocol has one. `project` is the block data (`Project.blocks.<protocol.build.blockField>`); the
 * polls reload it. Fields, route and actions come from `protocol.build` (`reportProtocols.js`).
 * The PDF is either a legacy list (`pdfField` with `pdfBuildStateField`) or a type of the unified
 * list (`pdfAttachmentType`, state from `attachmentBuildStates`). In Legacy-Authz a guest does not
 * build, and with `hidesDownloadsForGuests` does not see the downloads, as before the block.
 */
export default {
  name: 'ProjectReportBuild',
  components: { AttachmentList, ButtonWait, ZipDownload },
  mixins: [AccessBlockMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
    protocol: { type: Object, required: true },
  },
  computed: {
    ...mapGetters('auth', ['isGuest', 'isOrganizationRoleNavigationEnabled']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    isHiddenForGuest() {
      return this.isGuest && !this.isOrganizationRoleNavigationEnabled;
    },
    hasDownloads() {
      return (
        Boolean(this.protocol.build.zipAttachmentType) &&
        !(this.isHiddenForGuest && this.protocol.build.hidesDownloadsForGuests)
      );
    },
    /** Unified type or legacy list name, as the list and the delete name the PDF. */
    pdfAttachmentType() {
      return this.protocol.build.pdfAttachmentType ?? this.protocol.build.pdfField;
    },
    pdfAttachments() {
      if (this.protocol.build.pdfAttachmentType) {
        return attachmentsOfType(this.project.attachments, this.protocol.build.pdfAttachmentType);
      }
      return this.project[this.protocol.build.pdfField];
    },
  },
  methods: {
    ...mapActions(['deleteProjectAttachment']),
    // The protocol's actions are dispatched by their configured name (`mapActions` needs fixed
    // names); `reportProtocols.spec.js` checks that each exists in the store.
    reloadBlock() {
      return this.$store.dispatch(this.protocol.build.reloadAction, this.project.number);
    },
    async startReportBuild() {
      await this.$store.dispatch(this.protocol.build.buildAction, this.project.id);
    },
    /** Build state for ButtonWait; null when the reload failed, so ButtonWait keeps polling. */
    async fetchBuildState() {
      const block = await this.reloadBlock();
      if (this.protocol.build.pdfAttachmentType) {
        return buildStateOfType(block, this.protocol.build.pdfAttachmentType);
      }
      return block?.[this.protocol.build.pdfBuildStateField] ?? null;
    },
    /** The store sets the changed list on the block (`UPDATE_PROJECT_ATTACHMENTS`). */
    onDeleteAttachment(event) {
      return this.deleteProjectAttachment({
        projectId: this.project.id,
        attachmentId: event.attachmentId,
        attachmentType: this.pdfAttachmentType,
      });
    },
  },
};
</script>
